import { opsArticles } from "@/mocks/opsKnowledge";
import { opsReports } from "@/mocks/opsReports";

function OpsOverviewPage() {
  // 简单汇总（无需后端）
  const totalArticles = opsArticles.length;
  const totalReports = opsReports.length;
  const generatedReports = opsReports.filter((r) => r.status === "generated").length;
  const incidentReports = opsReports.filter((r) => r.type === "incident").length;

  const recentArticles = [...opsArticles]
    .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
    .slice(0, 5);
  const recentReports = [...opsReports]
    .sort((a, b) => (a.generatedAt < b.generatedAt ? 1 : -1))
    .slice(0, 5);

  const stats = [
    { label: "知识条目", value: totalArticles, color: "text-teal-700", bg: "bg-teal-50", border: "border-teal-200" },
    { label: "报告总数", value: totalReports, color: "text-indigo-700", bg: "bg-indigo-50", border: "border-indigo-200" },
    { label: "已生成报告", value: generatedReports, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
    { label: "事件报告", value: incidentReports, color: "text-rose-700", bg: "bg-rose-50", border: "border-rose-200" },
  ];

  const modules = [
    {
      title: "设备管理",
      desc: "AI 眼镜与配对手机的设备账本（电量、固件、在线状态、配对关系）",
      icon: "ri-glasses-line",
      path: "/devices",
      iconBg: "bg-teal-50",
      iconColor: "text-teal-600",
    },
    {
      title: "游客与行程",
      desc: "船次/团组/游客记录与问答轨迹",
      icon: "ri-suitcase-line",
      path: "/trips",
      iconBg: "bg-amber-50",
      iconColor: "text-amber-600",
    },
    {
      title: "数据分析",
      desc: "运营数据、设备利用率、问答热度分析",
      icon: "ri-bar-chart-box-line",
      path: "/analytics",
      iconBg: "bg-cyan-50",
      iconColor: "text-cyan-600",
    },
    {
      title: "外采数据",
      desc: "GPS 轨迹与人工标注点位导入、转化为景点",
      icon: "ri-route-line",
      path: "/tracking",
      iconBg: "bg-lime-50",
      iconColor: "text-lime-600",
    },
    {
      title: "运维知识库",
      desc: "SOP、故障排查、固件升级等运维知识文档",
      icon: "ri-book-read-line",
      path: "/ops/knowledge",
      iconBg: "bg-indigo-50",
      iconColor: "text-indigo-600",
    },
    {
      title: "运维报告",
      desc: "日报、周报、月报与事件分析报告",
      icon: "ri-file-list-3-line",
      path: "/ops/reports",
      iconBg: "bg-rose-50",
      iconColor: "text-rose-600",
    },
    {
      title: "系统配置",
      desc: "LLM / Embedding / ASR / TTS 等供应商配置与连通性测试",
      icon: "ri-settings-4-line",
      path: "/settings",
      iconBg: "bg-slate-100",
      iconColor: "text-slate-700",
    },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">运维管理 · 总览</h1>
        <p className="text-sm text-gray-500 mt-1">运维知识库与各类报告的统一入口</p>
      </div>

      {/* 数据汇总 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        {stats.map((s) => (
          <div key={s.label} className={`${s.bg} border ${s.border} rounded-lg p-4`}>
            <div className="text-xs text-gray-500 mb-1">{s.label}</div>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* 模块入口 */}
      <div>
        <h2 className="text-base font-semibold text-gray-900 mb-3">模块入口</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
          {modules.map((m) => (
            <a
              key={m.path}
              href={m.path}
              className="group bg-white border border-gray-200 rounded-lg p-5 hover:border-teal-300 hover:shadow-md transition-all"
            >
              <div className={`w-11 h-11 ${m.iconBg} rounded-lg flex items-center justify-center mb-3`}>
                <i className={`${m.icon} text-xl ${m.iconColor}`} />
              </div>
              <div className="text-sm font-semibold text-gray-900 mb-1 group-hover:text-teal-700">{m.title}</div>
              <div className="text-xs text-gray-500 leading-relaxed">{m.desc}</div>
              <div className="mt-3 text-xs text-teal-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                进入管理 <i className="ri-arrow-right-line" />
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* 最近更新 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">最近更新的知识</h3>
            <a href="/ops/knowledge" className="text-xs text-teal-600 hover:text-teal-700 font-medium">
              全部 <i className="ri-arrow-right-line" />
            </a>
          </div>
          <div className="space-y-2">
            {recentArticles.map((a) => (
              <a
                key={a.id}
                href="/ops/knowledge"
                className="block border border-gray-200 rounded-lg p-3 hover:border-teal-300 hover:bg-teal-50/20 transition-colors"
              >
                <div className="text-sm font-medium text-gray-900 truncate">{a.title}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {a.category} · {a.author} · {a.updatedAt}
                </div>
              </a>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-900">最近生成的报告</h3>
            <a href="/ops/reports" className="text-xs text-teal-600 hover:text-teal-700 font-medium">
              全部 <i className="ri-arrow-right-line" />
            </a>
          </div>
          <div className="space-y-2">
            {recentReports.map((r) => (
              <a
                key={r.id}
                href="/ops/reports"
                className="block border border-gray-200 rounded-lg p-3 hover:border-teal-300 hover:bg-teal-50/20 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium text-gray-900 truncate">{r.title}</div>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                      r.status === "generated"
                        ? "bg-emerald-100 text-emerald-700"
                        : r.status === "pending"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {r.status === "generated" ? "已生成" : r.status === "pending" ? "生成中" : "已归档"}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">{r.generatedAt}</div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OpsOverviewPage;
